import { useUpdate, useCreate, useNotify, useRefresh } from "ra-core";
import { useCallback, useState } from "react";

interface Job {
  id: string;
  contact_id: string | number;
  job_number?: string;
  status: string;
  title: string;
}

const VALID_TRANSITIONS: Record<string, string[]> = {
  created: ["scheduled", "in_progress"],
  scheduled: ["in_progress", "created"],
  in_progress: ["completed", "scheduled"],
  completed: ["closed", "in_progress"],
  closed: [],
};

/**
 * Hook providing status-transition actions for jobs.
 * Validates transitions and logs timeline events.
 */
export function useJobActions() {
  const [update] = useUpdate();
  const [create] = useCreate();
  const notify = useNotify();
  const refresh = useRefresh();
  const [isPending, setIsPending] = useState(false);

  const transition = useCallback(
    async (job: Job, toStatus: string, extra?: Record<string, unknown>) => {
      const allowed = VALID_TRANSITIONS[job.status] || [];
      if (!allowed.includes(toStatus)) {
        notify(`Cannot move from ${job.status} to ${toStatus}`, {
          type: "warning",
        });
        return;
      }

      setIsPending(true);
      const now = new Date().toISOString();
      try {
        const updateData: Record<string, unknown> = {
          status: toStatus,
          ...extra,
        };

        // Set lifecycle timestamps
        if (toStatus === "in_progress" && !extra?.actual_start) {
          updateData.actual_start = now.split("T")[0];
        }
        if (toStatus === "completed") {
          updateData.completed_at = now;
          updateData.actual_end = now.split("T")[0];
        }
        if (toStatus === "closed") {
          updateData.closed_at = now;
        }

        await update("jobs", {
          id: job.id,
          data: updateData,
          previousData: { id: job.id },
        });

        await create("events", {
          data: {
            contact_id: job.contact_id,
            job_id: job.id,
            event_type: "job_status_changed",
            payload: {
              job_id: job.id,
              job_number: job.job_number,
              title: job.title,
              from_status: job.status,
              to_status: toStatus,
              timestamp: now,
            },
          },
        });

        notify(
          `${job.job_number || "Job"} → ${toStatus.replace("_", " ")}`,
          { type: "success" }
        );
        refresh();
      } catch (err) {
        notify(`Action failed: ${(err as Error).message}`, { type: "error" });
      } finally {
        setIsPending(false);
      }
    },
    [update, create, notify, refresh]
  );

  return { transition, isPending, VALID_TRANSITIONS };
}
